import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  School,
  User,
  Mail,
  Phone,
  MapPin,
  Image as ImageIcon,
  PenTool,
  ArrowRight,
  ChevronRight,
} from "lucide-react";
import logo from "../assets/white_logo.png";
import { CircleLoader } from "../components/CircleLoader";
import { AppDispatch } from "../app/store";
import { registerThunk } from "../features/actions";
import {
  uploadAccept,
  uploadImageToCloudinary,
  validateUploadFile,
} from "../utilities/importantFns";

interface DetailsInterface {
  school_name: string;
  ceo_name: string;
  address: string;
  phone_number: string;
  package_subscribed: string;
  school_logo: File | null;
  surname: string;
  firstname: string;
  email: string;
  name: string;
  school_head_signature: File | null;
}

export const Register = () => {
  const [loading, setLoading] = useState(false);
  const [signUp, setSignUp] = useState<DetailsInterface>({
    name: "",
    school_name: "",
    ceo_name: "",
    address: "",
    phone_number: "",
    school_logo: null,
    surname: "",
    firstname: "",
    email: "",
    package_subscribed: "starter",
    school_head_signature: null,
  });

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setSignUp({ ...signUp, [name]: value });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const validation = validateUploadFile(file, "image");
      if (!validation.valid) {
        toast.error(validation.message);
        e.target.value = "";
        return;
      }
    }
    setSignUp({ ...signUp, school_logo: file });
  };

  const handleSignatureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (file) {
      const validation = validateUploadFile(file, "image");
      if (!validation.valid) {
        toast.error(validation.message);
        e.target.value = "";
        return;
      }
    }
    setSignUp({ ...signUp, school_head_signature: file });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      let uploadedImageUrl = "";
      let uploadedSignatureUrl = "";

      if (signUp.school_logo) {
        uploadedImageUrl = await uploadImageToCloudinary(signUp.school_logo);
      }
      if (signUp.school_head_signature) {
        uploadedSignatureUrl = await uploadImageToCloudinary(
          signUp.school_head_signature,
        );
      }

      const formData = new FormData();
      formData.append("school_name", signUp.school_name);
      formData.append("name", signUp.name);
      formData.append("ceo_name", signUp.ceo_name);
      formData.append("address", signUp.address);
      formData.append("package_subscribed", "starter");
      formData.append("phone_number", signUp.phone_number || "");
      formData.append("surname", signUp.surname);
      formData.append("firstname", signUp.firstname);
      formData.append("email", signUp.email);

      if (uploadedImageUrl) formData.append("school_logo", uploadedImageUrl);
      if (uploadedSignatureUrl)
        formData.append("school_head_signature", uploadedSignatureUrl);

      const result = await dispatch(registerThunk(formData)).unwrap();
      setLoading(false);

      if (typeof result === "string") throw new Error(result);

      toast.success("Registration successful!");
      navigate("/login");
    } catch (error: any) {
      setLoading(false);
      const errorMessage =
        error.response?.data?.message || error.message || "Registration failed";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (window.sessionStorage.getItem("token")) {
      navigate("/admin");
    }
  }, [navigate]);

  // Design Tokens
  const sectionTitle =
    "text-[12px] font-black text-blue-600 uppercase tracking-[0.2em] mb-6 flex items-center gap-2";
  const labelStyle =
    "text-[11px] font-black text-slate-400 uppercase tracking-[0.15em] mb-2 block ml-1";
  const inputContainer =
    "relative flex items-center bg-slate-50 rounded-2xl border-2 border-transparent focus-within:border-blue-500 focus-within:bg-white transition-all px-4 py-3";
  const inputStyle =
    "w-full bg-transparent outline-none text-slate-700 font-bold text-sm placeholder:text-slate-300";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen bg-white">
      {/* Left Pane - Shared Branding */}
      <div className="hidden lg:flex flex-col justify-between p-16 bg-[#012063] relative overflow-hidden h-screen sticky top-0">
        <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="relative z-10">
          <img src={logo} alt="Logo" className="h-12 w-auto" />
          <div className="w-12 h-1 bg-blue-400 mt-4 rounded-full" />
        </div>
        <div className="relative z-10 max-w-md">
          <h4 className="text-blue-400 font-black text-xs uppercase tracking-[0.3em] mb-4">
            Institutional Onboarding
          </h4>
          <p className="text-white text-3xl font-bold leading-tight">
            Empower your educators and transform your school's digital workflow.
          </p>
        </div>
        <div className="relative z-10 text-blue-300 text-xs font-medium tracking-widest uppercase">
          © 2026 Education System • Version 3.0
        </div>
      </div>

      {/* Right Pane - Scrollable Form */}
      <div className="flex flex-col items-center py-12 px-6 lg:px-20 relative bg-white overflow-y-auto">
        {/* Subtle Background Pattern */}
        <div
          className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(#012063 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />

        <div className="w-full max-w-2xl relative z-10">
          <div className="mb-12">
            <h2 className="text-3xl font-black text-slate-900 tracking-tighter">
              Create Account
            </h2>
          </div>

          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Section: School Info */}
            <div>
              <h3 className={sectionTitle}>
                <School size={16} /> School Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className={labelStyle}>Institutional Name</label>
                  <div className={inputContainer}>
                    <School className="text-slate-300 mr-3" size={18} />
                    <input
                      name="school_name"
                      value={signUp.school_name}
                      onChange={handleChange}
                      placeholder="Elite International School"
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className={labelStyle}>Official Address</label>
                  <div className={inputContainer}>
                    <MapPin className="text-slate-300 mr-3" size={18} />
                    <input
                      name="address"
                      value={signUp.address}
                      onChange={handleChange}
                      placeholder="123 Education Way"
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className={labelStyle}>School Logo</label>
                  <div className="relative group">
                    <input
	                      type="file"
	                      accept={uploadAccept.image}
	                      onChange={handleFileChange}
                      className="absolute inset-0 opacity-0 cursor-pointer z-20"
                      required
                    />
                    <div
                      className={`${inputContainer} group-hover:border-blue-300 transition-colors`}
                    >
                      <ImageIcon className="text-slate-300 mr-3" size={18} />
                      <span className="text-slate-400 text-sm font-bold">
                        {signUp.school_logo
                          ? signUp.school_logo.name
                          : "Upload PNG/JPG"}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="space-y-1">
                  <label className={labelStyle}>CEO/Proprietor Name</label>
                  <div className={inputContainer}>
                    <User className="text-slate-300 mr-3" size={18} />
                    <input
                      name="ceo_name"
                      value={signUp.ceo_name}
                      onChange={handleChange}
                      placeholder="Dr. Jane Smith"
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section: Admin Details */}
            <div>
              <h3 className={sectionTitle}>
                <User size={16} /> Primary Administrator
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className={labelStyle}>Surname</label>
                  <div className={inputContainer}>
                    <input
                      name="surname"
                      value={signUp.surname}
                      onChange={handleChange}
                      placeholder="Surname"
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className={labelStyle}>First Name</label>
                  <div className={inputContainer}>
                    <input
                      name="firstname"
                      value={signUp.firstname}
                      onChange={handleChange}
                      placeholder="First Name"
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className={labelStyle}>Work Email</label>
                  <div className={inputContainer}>
                    <Mail className="text-slate-300 mr-3" size={18} />
                    <input
                      name="email"
                      type="email"
                      value={signUp.email}
                      onChange={handleChange}
                      placeholder="admin@school.com"
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className={labelStyle}>Phone Number</label>
                  <div className={inputContainer}>
                    <Phone className="text-slate-300 mr-3" size={18} />
                    <input
                      name="phone_number"
                      type="tel"
                      value={signUp.phone_number}
                      onChange={handleChange}
                      placeholder="+234..."
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <label className={labelStyle}>Username</label>
                  <div className={inputContainer}>
                    <User className="text-slate-300 mr-3" size={18} />
                    <input
                      name="name"
                      value={signUp.name}
                      onChange={handleChange}
                      placeholder="j.smith"
                      className={inputStyle}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className={labelStyle}>Digital Signature</label>
                  <div className="relative group">
                    <input
	                      type="file"
	                      accept={uploadAccept.image}
	                      onChange={handleSignatureChange}
                      className="absolute inset-0 opacity-0 cursor-pointer z-20"
                      required
                    />
                    <div
                      className={`${inputContainer} group-hover:border-blue-300 transition-colors`}
                    >
                      <PenTool className="text-slate-300 mr-3" size={18} />
                      <span className="text-slate-400 text-sm font-bold">
                        {signUp.school_head_signature
                          ? "Signature Uploaded"
                          : "Upload Signature"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-slate-100">
              <button
                type="submit"
                disabled={loading}
                className="w-full h-16 bg-[#012063] text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-blue-900/20 hover:bg-[#082a7a] hover:-translate-y-1 transition-all flex items-center justify-center gap-3 active:scale-95"
              >
                {loading ? (
                  <CircleLoader />
                ) : (
                  <>
                    Register <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-center mt-8 text-slate-400 text-xs font-bold uppercase tracking-widest">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="text-blue-600 hover:text-blue-800 ml-1"
                >
                  Login here
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
